import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { RMWContext } from 'app/shared/providers/RMWContext';

import {
    Flex,
    IconArrowRightCircle,
    Spacer,
    Text,
    TextButton,
    Tooltip,
    TooltipPopper,
} from '@zillow/constellation';

import accessUtils from 'app/shared/utils/accessUtils';
import analyticsUtils from 'app/client/utils/analyticsUtils';

import ModalActions from 'app/shared/actions/ModalActions';

import ModalConstants from 'app/shared/constants/ModalConstants';
import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';

import { AppSelectors } from 'app/shared/selectors';

const propTypes = {
    formData: PropTypes.object.isRequired,
    title: PropTypes.string.isRequired,
};

const ListingHeader = ({
    formData,
    title,
}) => {
    const dispatch = useDispatch();
    const { paths } = React.useContext(RMWContext);
    const appConfig = useSelector(state => AppSelectors.makeAppConfig(state));
    const cookieEnabledServices = useSelector(state => AppSelectors.makeCookieEnabledServices(state));
    const {
        isFeed,
        listingAlias,
        topDupesDetails,
    } = formData;

    const canFeedConnectSwitchSource = accessUtils.canFeedConnectSwitchSource({ appConfig, cookieEnabledServices });
    const showListingSource = canFeedConnectSwitchSource && !isFeed
        && topDupesDetails && topDupesDetails.dupes.length > 0;
    const showListingIssues = canFeedConnectSwitchSource && isFeed;

    const handleGoToListingSource = () => {
        dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.MANAGE_LISTING_SOURCE_CLICK));
        const url = `${paths.propertiesPage}/${listingAlias}/${paths.propertyDetailsSources}`;
        window.router.transitionTo(url);
    };

    const handleListingIssueClick = () => {
        dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.VIEW_LISTING_ISSUES_MODAL));
        dispatch(ModalActions.showModal({
            modalType: ModalConstants.LISTING_ISSUES_MODAL,
            header: 'Listing issues',
            isFluid: true,
        }));
    };

    const tooltipContent = (
        <Tooltip
            body={(
                <Text fontType="finePrint">
                    You have one or more other versions of this listing. Those listings come from other sources,
                    such as feeds, and may be active on other sites.
                </Text>
            )}
        />
    );

    return (
        <Flex
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            mediaQuery={{
                md: {
                    flexDirection: 'row',
                    alignItems: 'center',
                },
                lg: {
                    paddingBottom: 'sm',
                },
            }}
        >
            <Flex flex="1" paddingBottom="xs">
                <Text
                    fontType="h4"
                    as="div"
                >
                    {title}
                </Text>
            </Flex>
            {showListingSource && (
                <Spacer paddingBottom="xs">
                    <TextButton onClick={handleGoToListingSource}>
                        Manage listing source
                    </TextButton>
                    &nbsp;
                    <TooltipPopper
                        triggered={tooltipContent}
                        placement="top"
                    />
                </Spacer>
            )}
            {showListingIssues && (
                <Spacer paddingBottom="xs">
                    <TextButton
                        onClick={handleListingIssueClick}
                        icon={<IconArrowRightCircle />}
                        iconPosition="end"
                    >
                        Listing Issues?
                    </TextButton>
                </Spacer>
            )}
        </Flex>
    );
};

ListingHeader.propTypes = propTypes;

export { ListingHeader };
export default ListingHeader;
